'use client';

import { cn } from '@ui/lib/utils';
import { Check, Copy } from 'lucide-react';
import * as React from 'react';
import { Input } from './input';
import { triggerSparkles } from './sparkles';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';

export interface ClickToCopyInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'onChange' | 'value' | 'size'
  > {
  value: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  iconClassName?: string;
  showLabel?: boolean;
  label?: string;
  emptyStateButton?: React.ReactNode;
}

const ClickToCopyInput = React.forwardRef<
  HTMLInputElement,
  ClickToCopyInputProps
>(
  (
    {
      value,
      size = 'sm',
      className,
      iconClassName,
      showLabel = true,
      label,
      emptyStateButton,
      ...props
    },
    ref
  ) => {
    const [copied, setCopied] = React.useState(false);
    const [tooltipOpen, setTooltipOpen] = React.useState(false);

    // Handle copy to clipboard with sparkles
    const handleCopy = React.useCallback(
      async (e?: React.MouseEvent) => {
        if (!value) return;

        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);

          // Show the "copied" tooltip immediately
          setTooltipOpen(true);

          // Hide tooltip and reset copied state after delay
          setTimeout(() => {
            setCopied(false);
            setTooltipOpen(false);
          }, 2000);

          // Trigger sparkles at the click position
          if (e && e.currentTarget) {
            const rect = e.currentTarget.getBoundingClientRect();
            const x = e.clientX;
            const y = e.clientY;
            triggerSparkles(x, y);
          }
        } catch (err) {
          console.error('Failed to copy text: ', err);
        }
      },
      [value]
    );

    // Handle Enter key press for copy
    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && value && e.currentTarget) {
          e.preventDefault();
          // For keyboard interaction, use the center of the input
          const rect = e.currentTarget.getBoundingClientRect();
          const centerX = rect.left + rect.width / 2;
          const centerY = rect.top + rect.height / 2;

          // Create a mock event with the center coordinates
          const mockEvent = {
            currentTarget: e.currentTarget,
            clientX: centerX,
            clientY: centerY,
          } as React.MouseEvent;

          handleCopy(mockEvent);
        }
      },
      [handleCopy, value]
    );

    // Handle input click
    const handleInputClick = React.useCallback(
      (e: React.MouseEvent<HTMLInputElement>) => {
        if (value) {
          handleCopy(e);
        }
      },
      [handleCopy, value]
    );

    // Handle icon click
    const handleIconClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation(); // Prevent event bubbling
        if (value) {
          handleCopy(e);
        }
      },
      [handleCopy, value]
    );

    // Size-based classes
    const sizeConfig = {
      xs: {
        height: 'h-7',
        iconSize: 'h-3 w-3',
        iconLeft: 'left-[8px]',
        paddingLeft: 'pl-7',
        text: 'text-xs',
      },
      sm: {
        height: 'h-8',
        iconSize: 'h-3.5 w-3.5',
        iconLeft: 'left-[10px]',
        paddingLeft: 'pl-8',
        text: 'text-xs',
      },
      md: {
        height: 'h-10',
        iconSize: 'h-4 w-4',
        iconLeft: 'left-[11px]',
        paddingLeft: 'pl-9',
        text: 'text-sm',
      },
      lg: {
        height: 'h-12',
        iconSize: 'h-5 w-5',
        iconLeft: 'left-[13px]',
        paddingLeft: 'pl-12',
        text: 'text-base',
      },
      xl: {
        height: 'h-14',
        iconSize: 'h-6 w-6',
        iconLeft: 'left-[15px]',
        paddingLeft: 'pl-14',
        text: 'text-lg',
      },
    };

    const currentSizeConfig = sizeConfig[size];

    // If no value and empty state button provided, show empty state
    if (!value && emptyStateButton) {
      return (
        <div className="space-y-2">
          {showLabel && label && (
            <p className="font-bold text-sm leading-6 text-slate-600">
              {label}
            </p>
          )}
          {emptyStateButton}
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {showLabel && label && (
          <p className="font-bold text-sm leading-6 text-slate-600">{label}</p>
        )}
        <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
          <TooltipTrigger asChild>
            <div className="relative">
              <Input
                ref={ref}
                value={value}
                readOnly
                onKeyDown={handleKeyDown}
                onClick={handleInputClick}
                className={cn(
                  'font-medium cursor-pointer',
                  currentSizeConfig.height,
                  currentSizeConfig.paddingLeft,
                  currentSizeConfig.text,
                  'focus:cursor-text',
                  className
                )}
                inputSize={size as any}
                {...props}
              />
              <button
                type="button"
                onClick={handleIconClick}
                disabled={!value}
                className={cn(
                  'absolute top-1/2 transform -translate-y-1/2 text-slate-600 hover:text-slate-800 transition-colors duration-200 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50',
                  currentSizeConfig.iconLeft,
                  iconClassName
                )}
              >
                {copied ? (
                  <Check
                    className={cn(currentSizeConfig.iconSize, 'text-green-600')}
                  />
                ) : (
                  <Copy className={currentSizeConfig.iconSize} />
                )}
              </button>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            {copied ? 'Copied to clipboard' : 'Click to copy'}
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }
);

ClickToCopyInput.displayName = 'ClickToCopyInput';

export { ClickToCopyInput };
